import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { FREE_SHIPPING_THRESHOLD, computeTotals, formatPLN } from '../../config/checkout';
import { useAuth } from '../../hooks/Services/AuthContext';
import { changeLanguage } from '../../Store/actions/languageActions';
import { setCurrency } from "../../Store/reducers/currencyReducer";
import { Accordion, AccordionSummary, AccordionDetails, IconButton, Select } from '@mui/material';
import Cookies from 'js-cookie';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import useFetch from '../../hooks/useFetch';
import CurrencySelector from "../CurrencySelector/CurrencySelector";
import Cart from '../Cart/Cart';
import AccountMenu from '../AccountMenu/AccountMenu';
import './NavBar.scss';
import logo from '../../assets/logo.png';
import NavBarSearch from "./NavBarSearch";
import Logo from '../Logo/Logo';
// import { Button } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import EssenceDrawer from '../Essences/EssenceDrawer';
import { useLocation, matchPath } from "react-router-dom";
import { updateQuantity } from '../../Store/reducers/cartReducer';
import { FormControl, MenuItem, useTheme } from '@mui/material';
import { ThemeProvider, createTheme, useColorScheme } from '@mui/material/styles';
import ThemeToggle from "../../ThemeToggle"
import Button from '@mui/material/Button'



const NavBar = ({ wishlistId, documentId, mode, setMode }) => {
    const { id, catId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { logout } = useAuth();
    // const { mode, setMode } = useColorScheme();


    // const theme = useTheme();
    const locale = useSelector(state => state.language.locale);
    const products = useSelector(state => state.cart.products);
    const rates = useSelector(state => state.currency.rates);

    const user = Cookies.get('user');
    const isLoggedIn = !!user;

    const [open, setOpen] = useState(false);
    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);


    // Fetch categories and their subcategories/essences
    const { data: categories } = useFetch(`/categories?filters[locale][$eq]=${locale}&populate=*`);

    const { data: subCategories } = useFetch(`/sub-categories?filters[locale][$eq]=${locale}&populate=*`);

    // Fetch categories
    const { data } = useFetch(
        `/categories?filters[locale][$eq]=${locale}&populate=*`
    );


    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const handleChangeLanguage = (e) => {
        dispatch(changeLanguage(e.target.value));
    };

    const handleCurrencyChange = e => {
        dispatch(setCurrency(e.target.value));
    };

    const toggleDrawer = (isOpen) => (event) => {
        if (
            event &&
            event.type === "keydown" &&
            (event.key === "Tab" || event.key === "Shift")
        ) {
            return;
        }
        setOpen(isOpen);
    };
    const fetchProducts = async (query) => {
        if (!query) return [];
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/products?filters[title][$containsi]=${encodeURIComponent(query)}&populate=*`
            );
            const json = await res.json();
            return json.data || [];
        } catch (e) {
            return [];
        }
    };

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // Selection lives in the URL (same source as Products page), so mobile and
    // desktop filters always stay in sync
    const activeCategoryId = matchPath("/products/:documentId", location.pathname)?.params.documentId;
    const selectedSubCats = searchParams.get("subCats")?.split(",").filter(Boolean) || [];

    const handleChange = (e, categoryId) => {
        const value = e.target.value;
        const isChecked = e.target.checked;

        // switching category starts a fresh selection
        const current = categoryId === activeCategoryId ? selectedSubCats : [];
        const updated = isChecked
            ? [...current, value]
            : current.filter((item) => item !== value);

        // update URL so Products reacts
        navigate({
            pathname: `/products/${categoryId}`,
            search: updated.length ? `?subCats=${updated.join(",")}` : ""
        });
    };

    // Clicking Home while already on Home remounts the page (loaders show again)
    // and scrolls to the top; see Layout in router.js
    const goHome = (e) => {
        if (location.pathname === "/") {
            e?.preventDefault();
            navigate("/", { replace: true, state: { refresh: Date.now() } });
        } else if (!e) {
            navigate("/");
        }
    };

    const cartDiscount = useSelector(state => state.cart.discount);
    const { missingForFreeShipping } = computeTotals(products, cartDiscount);

    return (
        <div className="navbar">
            {/* Free shipping announcement; turns into a countdown once the bag has items. */}
            <div className="free-shipping-bar" role="status">
                {products.length === 0 ? (
                    <FormattedMessage id="free_shipping_from" values={{ amount: formatPLN(FREE_SHIPPING_THRESHOLD) }} />
                ) : missingForFreeShipping > 0 ? (
                    <FormattedMessage
                        id="free_shipping_missing"
                        values={{ amount: <b>{formatPLN(missingForFreeShipping)}</b> }}
                    />
                ) : (
                    <FormattedMessage id="free_shipping_reached" />
                )}
            </div>
            <div className="wrapper">
                {/* Hamburger (mobile only via CSS) */}
                <div className="mobile-menu-button">
                    <button onClick={() => setMobileMenuOpen(true)} aria-label={intl.formatMessage({ id: "menu", defaultMessage: "Menu" })}>
                        <MenuIcon />
                    </button>
                </div>

                {/* Desktop utilities: language, currency, theme */}
                <div className="left">
                    <FormControl size="small">
                        <Select
                            size="small"
                            variant="outlined"
                            value={locale}
                            onChange={handleChangeLanguage}
                        >
                            <MenuItem value="pl">Polski</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="fr">Français</MenuItem>
                            <MenuItem value="de">Deutsch</MenuItem>
                            <MenuItem value="sp">Español</MenuItem>
                            <MenuItem value="hi">हिन्दी</MenuItem>
                            <MenuItem value="ru">Русский</MenuItem>
                        </Select>
                    </FormControl>
                    <span className="divider" aria-hidden="true" />
                    <CurrencySelector />
                    <span className="divider" aria-hidden="true" />
                    <ThemeToggle mode={mode} setMode={setMode} />
                </div>

                <div className="center">
                    <Link className="logo" to="/" onClick={goHome} aria-label="Carlo Bossi">
                        <span className="logo__word">Carlo Bossi</span>
                        <span className="logo__sub">Parfums</span>
                    </Link>
                </div>

                {/* Desktop right: icons always visible */}
                <div className="right">
                    <div className="icons">
                        <div className="search">
                            <NavBarSearch />
                        </div>
                        <AccountMenu isLoggedIn={isLoggedIn} onClick={handleLogout} />
                        <IconButton className="wishlist" onClick={() => navigate(`/wishlists/me`)} aria-label={intl.formatMessage({ id: "wishlist_title", defaultMessage: "Wishlist" })}>
                            <FavoriteBorderOutlinedIcon />
                        </IconButton>
                        <Box className="cartIcon">
                            <IconButton
                                onClick={() => setCartDrawerOpen((isOpen) => !isOpen)}
                                aria-label={intl.formatMessage({ id: "cart", defaultMessage: "Cart" })}
                                aria-expanded={cartDrawerOpen}
                            >
                                <ShoppingBagOutlinedIcon />
                                {products.length > 0 && (
                                    <span className="bag-count">
                                        {products.reduce((sum, item) => sum + item.quantity, 0)}
                                    </span>
                                )}
                            </IconButton>
                            <Cart
                                open={cartDrawerOpen}
                                onClose={() => setCartDrawerOpen(false)}
                                onOpen={() => setCartDrawerOpen(true)}
                            />
                        </Box>
                    </div>
                </div>
            </div>

            {/* Desktop navigation row */}
            <nav className="nav-row">
                <Button component={Link} to="/" onClick={goHome}>
                    <FormattedMessage id="homepage" />
                </Button>
                {data?.map((category) => (
                    <Button
                        key={category.documentId}
                        component={Link}
                        to={`/products/${category.documentId}`}
                    >
                        {category.title}
                    </Button>
                ))}
                <Button component={Link} to="/products">
                    <FormattedMessage id="stores" />
                </Button>
                <Button component={Link} to="/about">
                    <FormattedMessage id="about" />
                </Button>
                <Button component={Link} to="/contact">
                    <FormattedMessage id="contact" />
                </Button>
            </nav>

            {/* Mobile Menu Drawer */}
            <SwipeableDrawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                onOpen={() => setMobileMenuOpen(true)}
                PaperProps={{
                    sx: {
                        width: '60vw',
                        minWidth: 220,
                        maxWidth: 240,
                        bgcolor: 'background.default',
                        top: 0,
                    }
                }}
            >
                <Box sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'var(--font-sans)',
                }}>
                    {/* Header Section */}

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}>
                        {/* Theme Toggle (optional) */}
                        <Box sx={{
                            marginTop: '1px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'

                        }}>
                            <Box sx={{
                                fontSize: '0.875rem',
                                color: 'text.primary',
                                fontWeight: 500
                            }}>
                            </Box>
                            <ThemeToggle mode={mode} setMode={setMode} />
                        </Box>
                        <Box sx={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: '1.35rem',
                            fontWeight: 400,
                            color: 'text.primary',
                            letterSpacing: '0.02em'
                        }}>
                            <FormattedMessage id="menu" defaultMessage="Menu" />
                        </Box>

                        <IconButton
                            onClick={() => setMobileMenuOpen(false)}
                            disableripple="true"
                            sx={{
                                padding: '8px',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '26px' }} />
                        </IconButton>
                    </Box>

                    {/* Scrollable Content */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px 0'
                    }}>
                        {/* Settings Section */}
                        <Box sx={{
                            padding: '0 20px 16px',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            marginBottom: '12px'
                        }}>
                            {/* Language & Currency */}
                            <Box sx={{
                                display: 'flex',
                                gap: '10px',
                                // alignItems: 'center'
                            }}>
                                {/* Language Selector */}
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <Select
                                        value={locale}
                                        onChange={handleChangeLanguage}
                                        disableripple="true"
                                        sx={{
                                            border: "none",
                                            fontSize: "0.7rem",
                                            // bgcolor: theme.palette.background.default,
                                            // color: theme.palette.text.primary,
                                            // bgcolor: "#fff",
                                            fontFamily: 'var(--font-sans)',
                                            borderRadius: "5px",
                                            mr: 1,
                                            textTransform: "uppercase",
                                            boxShadow: "none",
                                            // height: "20px",
                                            width: "70px",


                                            "& .MuiOutlinedInput-notchedOutline": {
                                                border: "none",
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                border: "none",
                                            },
                                        }}
                                    >
                                        <MenuItem value="pl">PL</MenuItem>
                                        <MenuItem value="en">EN</MenuItem>
                                        <MenuItem value="fr">FR</MenuItem>
                                        <MenuItem value="de">DE</MenuItem>
                                        <MenuItem value="sp">ES</MenuItem>
                                        <MenuItem value="hi">HI</MenuItem>
                                        <MenuItem value="ru">RU</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Currency Selector */}
                                <Box sx={{ flex: 1 }}>
                                    <CurrencySelector />
                                </Box>

                            </Box>


                        </Box>

                        {/* Categories Button - BIGGER SIZE */}
                        <Button
                            fullWidth
                            disableripple="true"
                            onClick={() => setDrawerOpen(true)}
                            sx={{
                                justifyContent: 'space-between',
                                padding: '16px 24px',
                                fontFamily: 'var(--font-serif)',
                                fontSize: '1.15rem',
                                fontWeight: 300,
                                letterSpacing: '0.01em',
                                color: 'text.primary',
                                textTransform: 'none',
                                borderRadius: 0,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                marginBottom: '8px',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                }
                            }}
                        >
                            <FormattedMessage id="categories" />
                        </Button>

                        {/* Navigation Links - BIGGER SIZE */}
                        <Box sx={{ padding: '0' }}>
                            {/* Home Link */}
                            <Button
                                fullWidth
                                disableripple="true"
                                onClick={() => {
                                    goHome();
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    padding: '16px 20px',
                                    fontFamily: 'var(--font-serif)',
                                    fontSize: '1.15rem',
                                    fontWeight: 300,
                                    letterSpacing: '0.01em',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    }
                                }}
                            >
                                <FormattedMessage id="homepage" />
                            </Button>

                            {/* About Link */}
                            <Button
                                fullWidth
                                disableripple="true"
                                onClick={() => {
                                    navigate('/about');
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    padding: '16px 20px',
                                    fontFamily: 'var(--font-serif)',
                                    fontSize: '1.15rem',
                                    fontWeight: 300,
                                    letterSpacing: '0.01em',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    }
                                }}
                            >
                                <FormattedMessage id="about" />
                            </Button>

                            {/* Contact Link */}
                            <Button
                                fullWidth
                                disableripple="true"
                                onClick={() => {
                                    navigate('/contact');
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    padding: '16px 20px',
                                    fontFamily: 'var(--font-serif)',
                                    fontSize: '1.15rem',
                                    fontWeight: 300,
                                    letterSpacing: '0.01em',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    }
                                }}
                            >
                                <FormattedMessage id="contact" />
                            </Button>

                            {/* Store Link */}
                            <Button
                                fullWidth
                                disableripple="true"
                                onClick={() => {
                                    navigate('/products');
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    padding: '16px 20px',
                                    fontFamily: 'var(--font-serif)',
                                    fontSize: '1.15rem',
                                    fontWeight: 300,
                                    letterSpacing: '0.01em',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    }
                                }}
                            >
                                <FormattedMessage id="stores" />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </SwipeableDrawer>


            {/* Categories Sub-Drawer */}
            <SwipeableDrawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onOpen={() => setDrawerOpen(true)}
                PaperProps={{
                    sx: {
                        width: '60vw',
                        minWidth: 220,
                        maxWidth: 240,
                        bgcolor: 'background.default',
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}>
                        <IconButton
                            onClick={() => setDrawerOpen(false)}
                            disableripple="true"
                            sx={{
                                marginRight: '12px',
                                padding: '8px'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box sx={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: '1.35rem',
                            fontWeight: 400,
                            color: 'text.primary',
                            letterSpacing: '0.02em'
                        }}>
                            <FormattedMessage id="categories" />
                        </Box>
                    </Box>

                    {/* Categories Content */}
                    <Box sx={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
                        <EssenceDrawer
                            categories={categories}
                            subCategories={subCategories}
                            activeCategoryId={activeCategoryId}
                            selectedSubCats={selectedSubCats}
                            onEssenceChange={handleChange}
                            navigateToCategory={(documentId) => {
                                navigate(`/products/${documentId}`);
                                setDrawerOpen(false);
                                setMobileMenuOpen(false);
                            }}
                        />
                    </Box>
                </Box>
            </SwipeableDrawer>

        </div >
    );
};

export default NavBar;
